function SectionHeading({ id, children }) {
  return (
    <h2 id={id} className="scroll-mt-24 pt-6 text-2xl font-bold text-[#0A2540]">
      {children}
    </h2>
  );
}

function Subheading({ children }) {
  return <h3 className="pt-2 text-lg font-bold text-[#0A2540]">{children}</h3>;
}

function Paragraph({ children }) {
  return <p className="text-base leading-7 text-slate-600">{children}</p>;
}

function PolicyList({ children }) {
  return (
    <ul className="list-disc space-y-2 pl-6 text-base leading-7 text-slate-600">
      {children}
    </ul>
  );
}

export default function PrivacyPolicy() {
  return (
    <div className="bg-white">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-radial-emerald" />
        <div className="absolute inset-0 bg-grid opacity-50" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600 mb-4">
            Privacy Policy
          </div>
          <h1 className="text-[4.1rem] font-bold text-[#0A2540] tracking-tight leading-[1.04] mb-6">
            Privacy Policy
          </h1>
          <div className="space-y-5">
            <p className="text-lg font-semibold text-[#0A2540]">Luxsh Technologies Ltd</p>
            <p className="text-lg text-slate-600 leading-relaxed">
              ICO registration reference: <span className="font-semibold text-slate-700">ZA784352</span>
            </p>
          </div>

          <article className="mt-12 space-y-5">
            <Subheading>Introduction</Subheading>
            <Paragraph>
              Welcome to Luxsh Technologies. This document details the organisation&apos;s privacy policy.
            </Paragraph>
            <Paragraph>
              Luxsh Technologies Limited is a private company limited by shares incorporated in England and
              Wales with registered number 12069038.
            </Paragraph>
            <Paragraph>
              On 25 May 2018 the Data Protection Act 1998 was replaced by the General Data Protection
              Regulation (EU) 2016/679 (&quot;GDPR&quot;). This policy has been prepared to comply with the
              requirements of the GDPR. The GDPR will be followed by additional national implementing law,
              regulations and secondary legislation, so this policy may be further amended to take into
              account any additional requirements. Please check back regularly.
            </Paragraph>
            <Paragraph>
              The company respects your privacy and is committed to protecting your personal data. This
              privacy policy will inform you how we look after your personal data, including when you visit
              our website (regardless of where you visit it from) at{" "}
              <a
                className="text-emerald-700 underline underline-offset-4 hover:text-emerald-800"
                href="https://cascadeplus.co.uk"
                target="_blank"
                rel="noreferrer"
              >
                cascadeplus.co.uk
              </a>
              , and tells you about your privacy rights and how the law protects you. Please read this
              privacy policy carefully.
            </Paragraph>
            <Paragraph>
              This privacy policy is provided in a layered format so you can go directly to the specific
              areas set out below. Please also use the Glossary to understand the meaning of some of the
              terms used in this privacy policy.
            </Paragraph>

            <nav aria-label="Privacy policy sections">
              <ul className="space-y-2 border-l-2 border-emerald-200 pl-5 text-sm font-bold uppercase tracking-wide">
                <li><a className="text-emerald-700 hover:text-emerald-800" href="#important-information">Important information and who we are</a></li>
                <li><a className="text-emerald-700 hover:text-emerald-800" href="#data-we-collect">The data we collect about you</a></li>
                <li><a className="text-emerald-700 hover:text-emerald-800" href="#data-collection">How is your personal data collected?</a></li>
                <li><a className="text-emerald-700 hover:text-emerald-800" href="#data-use">How we use your personal data</a></li>
                <li><a className="text-emerald-700 hover:text-emerald-800" href="#disclosures">Disclosures of your personal data</a></li>
                <li><a className="text-emerald-700 hover:text-emerald-800" href="#data-security">Data security</a></li>
                <li><a className="text-emerald-700 hover:text-emerald-800" href="#data-retention">Data retention</a></li>
                <li><a className="text-emerald-700 hover:text-emerald-800" href="#legal-rights">Your legal rights</a></li>
                <li><a className="text-emerald-700 hover:text-emerald-800" href="#glossary">Glossary</a></li>
              </ul>
            </nav>

            <SectionHeading id="important-information">
              1. Important information and who we are
            </SectionHeading>
            <Subheading>Purpose of this privacy policy</Subheading>
            <Paragraph>
              This privacy policy aims to give you information on how Luxsh Technologies Ltd collects and
              processes your personal data through your use of this website and our tablet devices,
              including any data you may provide when you sign up to our newsletter or purchase a product
              or service. Read this policy together with any other privacy or fair-processing policy we
              provide when collecting or processing your personal data. This policy supplements those
              policies and is not intended to override them.
            </Paragraph>
            <Subheading>Controller</Subheading>
            <Paragraph>
              Luxsh Technologies Ltd is the controller responsible for your personal data. Our data
              protection officer (DPO) oversees questions about this policy. Please contact the DPO with
              questions or requests to exercise your legal rights.
            </Paragraph>
            <Subheading>Contact details</Subheading>
            <div className="space-y-1 text-base leading-7 text-slate-600">
              <p><strong className="text-[#0A2540]">Full name of legal entity:</strong> Luxsh Technologies Ltd</p>
              <p><strong className="text-[#0A2540]">Data Protection Officer:</strong> Chris Silveira</p>
            </div>
            <Paragraph>
              If you have a complaint or query about privacy or data protection, please contact us. You may
              also complain to the Information Commissioner&apos;s Office (ICO).
              The ICO helpline is 0303 123 1113; more information is available at{" "}
              <a className="text-emerald-700 underline" href="https://ico.org.uk" target="_blank" rel="noreferrer">ico.org.uk</a>.
            </Paragraph>
            <Subheading>Changes to this policy and your duty to inform us of changes</Subheading>
            <Paragraph>
              This policy was originally updated on 25 May 2018. Historic versions are available by
              contacting us. It is important that the personal data we hold about you is accurate and
              current, so please tell us if it changes during your relationship with us.
            </Paragraph>
            <Subheading>Third-party links</Subheading>
            <Paragraph>
              This website may include links to third-party websites, plug-ins and applications. Those
              third parties may collect or share data about you. We do not control them and are not
              responsible for their privacy statements. We encourage you to read the privacy policy of
              every website you visit.
            </Paragraph>

            <SectionHeading id="data-we-collect">2. The data we collect about you</SectionHeading>
            <Paragraph>
              Personal data means information from which an individual can be identified. It does not
              include data where the identity has been removed (anonymous data). We may collect, use, store
              and transfer the following kinds of personal data:
            </Paragraph>
            <PolicyList>
              <li><strong>Identity Data</strong> including name, username or similar identifier, marital status, title, date of birth and gender.</li>
              <li><strong>Contact Data</strong> including billing and delivery addresses, email address and telephone numbers.</li>
              <li><strong>Financial Data</strong> including bank account and payment card details.</li>
              <li><strong>Transaction Data</strong> including details of payments and products or services purchased from us.</li>
              <li><strong>Technical Data</strong> including IP address, login data, browser, time zone, location, operating system, platform and device technology.</li>
              <li><strong>Profile Data</strong> including your username, password, purchases, interests, preferences, feedback and survey responses.</li>
              <li><strong>Usage Data</strong> about how you use our website, products and services.</li>
              <li><strong>Marketing and Communications Data</strong> including your marketing and communication preferences.</li>
            </PolicyList>
            <Paragraph>
              We also collect, use and share <strong>Aggregated Data</strong>, such as statistical or
              demographic data. Aggregated Data may be derived from your personal data but is not personal
              data in law because it does not directly or indirectly reveal your identity. If we combine
              Aggregated Data with personal data so that it can identify you, we treat the combined data as
              personal data under this policy.
            </Paragraph>
            <Subheading>If you fail to provide personal data</Subheading>
            <Paragraph>
              Where we need personal data by law or under a contract and you do not provide it, we may be
              unable to perform that contract or provide the relevant product or service. We will notify
              you if we need to cancel a product or service for this reason.
            </Paragraph>

            <SectionHeading id="data-collection">3. How is your personal data collected?</SectionHeading>
            <Paragraph>We collect data from and about you through:</Paragraph>
            <PolicyList>
              <li>
                <strong>Direct interactions.</strong> You may give us Identity, Contact and Financial Data
                by filling in forms or corresponding with us by post, phone, email or otherwise, including
                when you apply for products or services; create an account; subscribe to services or
                publications; request marketing; join a webinar; enter a competition, promotion or survey;
                or give feedback.
              </li>
              <li>
                <strong>Automated technologies or interactions.</strong> As you use our website, we may
                collect Technical Data about your equipment, browsing actions and patterns using cookies,
                server logs and similar technologies.
              </li>
              <li>
                <strong>Third parties or public sources.</strong> We may receive Technical Data from
                analytics providers, advertising networks and search providers; Financial and Transaction
                Data from technical, payment and delivery providers; and Identity and Contact Data from
                data brokers, Companies House and the Electoral Register.
              </li>
            </PolicyList>
            <SectionHeading id="data-use">4. How we use your personal data</SectionHeading>
            <Paragraph>
              We will only use your personal data when the law allows us to, most commonly to perform a
              contract with you, for our legitimate interests where your rights do not override them, or to
              comply with a legal or regulatory obligation. We generally rely on consent only for
              third-party direct marketing by email or text. You may withdraw marketing consent at any time.
            </Paragraph>
            <Subheading>Purposes for which we will use your personal data</Subheading>
            <Paragraph>
              The table below describes how we plan to use your personal data and the legal bases we rely
              on. We may process data on more than one lawful ground depending on the purpose.
            </Paragraph>
            <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white/80">
              <table className="min-w-[760px] w-full text-left text-sm leading-6 text-slate-600">
                <thead className="bg-slate-50 text-[#0A2540]">
                  <tr>
                    <th className="px-4 py-3 font-bold">Purpose/activity</th>
                    <th className="px-4 py-3 font-bold">Type of data</th>
                    <th className="px-4 py-3 font-bold">Lawful basis</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  <tr className="align-top">
                    <td className="px-4 py-4">Register you as a new customer</td>
                    <td className="px-4 py-4">Identity; Contact</td>
                    <td className="px-4 py-4">Performance of a contract with you</td>
                  </tr>
                  <tr className="align-top">
                    <td className="px-4 py-4">Process and deliver your order, manage payments and recover money owed</td>
                    <td className="px-4 py-4">Identity; Contact; Financial; Transaction; Marketing and Communications</td>
                    <td className="px-4 py-4">Performance of a contract; legitimate interests in recovering debts</td>
                  </tr>
                  <tr className="align-top">
                    <td className="px-4 py-4">Manage our relationship with you, including policy notices, reviews and surveys</td>
                    <td className="px-4 py-4">Identity; Contact; Profile; Marketing and Communications</td>
                    <td className="px-4 py-4">Contract; legal obligation; legitimate interests in record keeping and understanding customers</td>
                  </tr>
                  <tr className="align-top">
                    <td className="px-4 py-4">Administer and protect our business and website</td>
                    <td className="px-4 py-4">Identity; Contact; Technical</td>
                    <td className="px-4 py-4">Legitimate interests in administration, IT, security, fraud prevention and restructuring; legal obligation</td>
                  </tr>
                  <tr className="align-top">
                    <td className="px-4 py-4">Deliver relevant content and advertising and measure its effectiveness</td>
                    <td className="px-4 py-4">Identity; Contact; Profile; Usage; Marketing and Communications; Technical</td>
                    <td className="px-4 py-4">Legitimate interests in understanding customers, developing services and informing marketing</td>
                  </tr>
                  <tr className="align-top">
                    <td className="px-4 py-4">Use analytics to improve our website, services, marketing and customer experience</td>
                    <td className="px-4 py-4">Technical; Usage</td>
                    <td className="px-4 py-4">Legitimate interests in defining customer types, keeping the website relevant and developing the business</td>
                  </tr>
                  <tr className="align-top">
                    <td className="px-4 py-4">Suggest goods or services that may interest you</td>
                    <td className="px-4 py-4">Identity; Contact; Technical; Usage; Profile</td>
                    <td className="px-4 py-4">Legitimate interests in developing our services and growing our business</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <Subheading>Marketing and promotional offers</Subheading>
            <Paragraph>
              We may use Identity, Contact, Technical, Usage and Profile Data to decide which products,
              services and offers may interest you. You may receive marketing if you requested information,
              bought goods or services, entered a competition or registered for a promotion and have not
              opted out. We will obtain your express opt-in before sharing personal data with a third party
              for marketing. You can ask us or a third party to stop marketing at any time. Opting out does
              not affect data provided through a purchase, service experience or other transaction.
            </Paragraph>
            <Subheading>Cookies</Subheading>
            <Paragraph>
              You can set your browser to refuse some or all cookies or alert you when websites access them.
              If you disable cookies, parts of this website may not function properly. For general
              information about cookies, visit{" "}
              <a className="text-emerald-700 underline" href="https://www.allaboutcookies.org" target="_blank" rel="noreferrer">allaboutcookies.org</a>.
            </Paragraph>
            <Subheading>Change of purpose</Subheading>
            <Paragraph>
              We will use personal data only for the purpose for which it was collected unless we reasonably
              consider another use compatible with the original purpose. Contact us for an explanation. If
              we need to use it for an unrelated purpose, we will notify you and explain the legal basis.
              We may process personal data without your knowledge or consent where the law requires or
              permits it.
            </Paragraph>
            <SectionHeading id="disclosures">5. Disclosures of your personal data</SectionHeading>
            <Paragraph>
              We may share personal data with Internal and External Third Parties described in the
              Glossary, specific parties connected to the purposes in section 4, and third parties involved
              in a sale, transfer, merger or acquisition of all or part of our business or assets. New
              owners may use personal data as described in this policy.
            </Paragraph>
            <Paragraph>
              We require third parties to respect the security of your personal data and treat it in
              accordance with the law. Service providers may use it only for specified purposes and under
              our instructions.
            </Paragraph>

            <SectionHeading id="data-security">6. Data security</SectionHeading>
            <Paragraph>
              We use appropriate security measures to prevent personal data from being accidentally lost,
              used, accessed, altered or disclosed without authorisation. Access is limited to employees,
              agents, contractors and other parties with a business need to know; they process it only on
              our instructions and are subject to confidentiality obligations.
            </Paragraph>
            <Paragraph>
              We have procedures for suspected personal data breaches and will notify you and any applicable
              regulator where legally required.
            </Paragraph>

            <SectionHeading id="data-retention">7. Data retention</SectionHeading>
            <Subheading>How long will you use my personal data for?</Subheading>
            <Paragraph>
              We retain personal data only as long as necessary for the purpose for which it was collected,
              including legal, accounting and reporting requirements. We consider the amount, nature and
              sensitivity of the data, the risks of unauthorised use or disclosure, the purpose of
              processing, available alternatives and applicable legal requirements.
            </Paragraph>
            <Paragraph>
              By law, we keep basic customer information including Contact, Identity, Financial and
              Transaction Data for six years after a person ceases to be a customer for tax purposes. In
              some circumstances you may ask us to delete your data. We may anonymise data for research or
              statistics and then use it indefinitely without further notice.
            </Paragraph>
            <SectionHeading id="legal-rights">8. Your legal rights</SectionHeading>
            <Paragraph>
              In certain circumstances, data protection law gives you the following rights:
            </Paragraph>
            <PolicyList>
              <li><strong>Request access</strong> to your personal data and check that we process it lawfully.</li>
              <li><strong>Request correction</strong> of incomplete or inaccurate personal data; we may need to verify the new information.</li>
              <li>
                <strong>Request erasure</strong> where there is no good reason for continued processing,
                where you successfully object, where processing is unlawful or where erasure is required by
                law. We may be unable to comply for specific legal reasons, which we will explain.
              </li>
              <li>
                <strong>Object to processing</strong> based on our or a third party&apos;s legitimate
                interests where your situation affects your fundamental rights and freedoms, and object to
                direct marketing. We may continue where we demonstrate compelling legitimate grounds.
              </li>
              <li>
                <strong>Request restriction of processing</strong> while accuracy is established, where use
                is unlawful but you do not want erasure, where you need data for legal claims, or while we
                verify whether our legitimate grounds override your objection.
              </li>
              <li>
                <strong>Request transfer</strong> of data you provided to you or a chosen third party in a
                structured, commonly used, machine-readable format where processing is automated and based
                on consent or a contract.
              </li>
              <li>
                <strong>Withdraw consent at any time</strong> where processing relies on consent. This does
                not affect processing carried out before withdrawal, but may prevent us providing some
                products or services.
              </li>
            </PolicyList>
            <Paragraph>If you wish to exercise any of these rights, please contact us.</Paragraph>
            <Subheading>No fee usually required</Subheading>
            <Paragraph>
              You will not usually pay a fee to access personal data or exercise your rights. We may charge
              a reasonable fee, or refuse a request, if it is clearly unfounded, repetitive or excessive.
            </Paragraph>
            <Subheading>What we may need from you</Subheading>
            <Paragraph>
              We may request information to confirm your identity and ensure personal data is not disclosed
              to someone with no right to receive it. We may also ask for more information to help speed up
              our response.
            </Paragraph>
            <Subheading>Time limit to respond</Subheading>
            <Paragraph>
              We try to respond to legitimate requests within one month. It may take longer if a request is
              particularly complex or you make several requests; in that case we will notify you and keep
              you updated.
            </Paragraph>
            <SectionHeading id="glossary">9. Glossary</SectionHeading>
            <Subheading>Lawful basis</Subheading>
            <Paragraph>
              <strong>Legitimate Interest</strong> means our interest in conducting and managing our
              business to provide the best and most secure service and experience. We consider and balance
              the potential impact on you and your rights before processing for legitimate interests. We do
              not use personal data where your rights override our interests unless we have consent or the
              law requires or permits it. Contact us for information about our assessments.
            </Paragraph>
            <Paragraph>
              <strong>Performance of Contract</strong> means processing necessary to perform a contract with
              you or take steps at your request before entering into one.
            </Paragraph>
            <Paragraph>
              <strong>Comply with a legal or regulatory obligation</strong> means processing necessary to
              meet a legal or regulatory obligation that applies to us.
            </Paragraph>
            <Subheading>Third parties</Subheading>
            <Paragraph>
              Other Luxsh Technologies companies may act as joint controllers or processors and provide IT
              and business administration services from India. External third parties include:
            </Paragraph>
            <PolicyList>
              <li>UK- and India-based service providers acting as processors for business processing, IT and system administration.</li>
              <li>Professional advisers such as lawyers, bankers, auditors and insurers providing consultancy, banking, legal, insurance and accounting services.</li>
              <li>HM Revenue &amp; Customs, regulators and other UK authorities that require reporting of processing activities.</li>
              <li>Industry regulators such as the Medicines and Healthcare products Regulatory Agency (MHRA).</li>
              <li>Fraud prevention agencies.</li>
            </PolicyList>
            <SectionHeading id="ico-certificate">ICO registration certificate</SectionHeading>
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50/70 p-5 sm:p-6">
              <dl className="grid gap-x-8 gap-y-4 text-base leading-7 text-slate-600 sm:grid-cols-2">
                <div>
                  <dt className="font-bold text-[#0A2540]">Registered organisation</dt>
                  <dd>Luxsh Technologies Limited</dd>
                </div>
                <div>
                  <dt className="font-bold text-[#0A2540]">Registration reference</dt>
                  <dd>ZA784352</dd>
                </div>
                <div>
                  <dt className="font-bold text-[#0A2540]">Date registered</dt>
                  <dd>1 September 2020</dd>
                </div>
                <div>
                  <dt className="font-bold text-[#0A2540]">Registration expires</dt>
                  <dd>31 August 2026</dd>
                </div>
                <div className="sm:col-span-2">
                  <dt className="font-bold text-[#0A2540]">Registered address</dt>
                  <dd>Argyle House, Joel Street, Northwood, HA6 1NW</dd>
                </div>
              </dl>
            </div>
          </article>
        </div>
      </section>
    </div>
  );
}
